import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocumentTitle } from 'usehooks-ts'
import { PageHeader } from '../../tailwindComponents/PageHeader'
import { FloxSpinner } from '@tailwindComponents/FloxSpinner'
import { NETWORK_RATECARD_UPDATE, NETWORK_RATECARD_GET } from '../../constants'
import { useAuthHttp } from '../../hooks'
import {
  DEFAULT_SERVICE,
  NetworkRateCardZone,
  Surcharge,
  createEmptyZone,
  DEFAULT_NETWORK_RATECARD_CURRENCY,
} from './types'
import { DEFAULT_COUNTRY } from './countries'
import { NetworkRatecardEditor } from './NetworkRatecardEditor'
import { fillEverySurchargeType } from './fillEverySurchargeType'

interface LoadedData {
  networkName: string
  countryCode: string
  currency: string
  effectiveFrom: string
  effectiveTo: string
  notes: string
  isActive: boolean
  isLocalRatecard: boolean
  zones: NetworkRateCardZone[]
  surcharges: Surcharge[]
}

export function EditNetworkRatecard() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const authHttp = useAuthHttp()
  useDocumentTitle('FLOX - Edit Network Rate Card')

  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loadedData, setLoadedData] = useState<LoadedData | null>(null)

  useEffect(() => {
    let isStillOnThisRatecard = true

    const fetchRateCard = async () => {
      setIsLoading(true)
      setLoadError(null)
      setLoadedData(null)
      try {
        const { data } = await authHttp.get(NETWORK_RATECARD_GET, {
          params: { id },
        })
        if (!isStillOnThisRatecard) return
        // Backend now returns a flat array of zone docs (one per zone of the requested
        // ratecard), mirroring the PlatformPostcodeRateCard shape. Network-level fields are
        // denormalised onto every zone — pick any zone for them.
        const zoneDocs: any[] = data?.zones || []
        if (zoneDocs.length === 0) {
          setLoadError('Network rate card not found')
          return
        }
        const head = zoneDocs[0]

        const loadedZones: NetworkRateCardZone[] = zoneDocs.map((zone: any) => ({
          id: crypto.randomUUID(),
          zoneName: zone.zoneName || '',
          countryCode: head.countryCode || DEFAULT_COUNTRY.code,
          locations: (zone.locations || []).map((l: any) => ({
            prefix: l.prefix,
            city: l.city || null,
            geoPoint: l.geoPoint || null,
          })),
          servicePricing: (zone.servicePricing || []).map((s: any) => ({
            serviceName: s.serviceName,
            unit: s.unit,
            breaks: (s.breaks || []).map((b: any) => ({
              min: b.min,
              max: b.max,
              price: b.price,
            })),
            // Carry pallet specs + unit value back so a load→re-save round-trip preserves
            // them. Previously dropped here, which silently wiped a service's pallet
            // size/dimensions (and the non-pallet UnitValue) the moment the user re-saved.
            ...(s.palletDimensions
              ? {
                  palletDimensions: {
                    height: s.palletDimensions.height ?? null,
                    width: s.palletDimensions.width ?? null,
                    depth: s.palletDimensions.depth ?? null,
                    weight: s.palletDimensions.weight ?? null,
                  },
                }
              : {}),
            ...(s.palletType ? { palletType: s.palletType } : {}),
            ...(s.unitValue != null ? { unitValue: s.unitValue } : {}),
          })),
        }))

        // Order rows by zone number. Natural/alphanumeric sort so "Zone 2" < "Zone 10" and "1" <
        // "2" < "41" (plain labels fall back to alphabetical). Done FE-side because the zone name is
        // a keyword field — an ES sort is lexicographic ("Zone 10" < "Zone 2") and a numeric script
        // sort would break on non-numeric names ("LOCAL", "Zone A").
        loadedZones.sort((a, b) =>
          a.zoneName.localeCompare(b.zoneName, undefined, { numeric: true, sensitivity: 'base' })
        )

        // Surcharges are network-wide and now denormalised onto every zone doc — read from
        // the first. Each gets a fresh client-side id for in-place edit/remove via the
        // SurchargesPanel; the BE identifies them positionally so the id never round-trips on save.
        const loadedSurcharges: Surcharge[] = (head.surcharges || []).map((s: any) => ({
          id: crypto.randomUUID(),
          name: s.name || '',
          price: typeof s.price === 'number' ? s.price : null,
          ...(s.unit ? { unit: s.unit } : {}),
          ...(s.notes ? { notes: s.notes } : {}),
          ...(s.selectableSurchargeType ? { selectableSurchargeType: s.selectableSurchargeType } : {}),
        }))

        setLoadedData({
          networkName: head.networkName || '',
          countryCode: head.countryCode || DEFAULT_COUNTRY.code,
          // BE always returns a populated `currency` for v3 ratecards (the migration backfills
          // "GBP" on older events), but defence-in-depth: fall back to the platform default if
          // the field is missing from a hand-edited record.
          currency: head.currency || DEFAULT_NETWORK_RATECARD_CURRENCY,
          effectiveFrom: head.effectiveFrom || '',
          effectiveTo: head.effectiveTo || '',
          notes: head.notes || '',
          isActive: head.isActive ?? true,
          isLocalRatecard: head.isLocalRatecard ?? false,
          zones: loadedZones.length > 0 ? loadedZones : [createEmptyZone('Zone 1', [DEFAULT_SERVICE], head.countryCode || DEFAULT_COUNTRY.code)],
          surcharges: fillEverySurchargeType(loadedSurcharges),
        })
      } catch (error) {
        console.error('Failed to fetch network ratecard:', error)
        if (isStillOnThisRatecard) setLoadError('Failed to load network rate card')
      } finally {
        if (isStillOnThisRatecard) setIsLoading(false)
      }
    }

    if (id) fetchRateCard()
    return () => {
      isStillOnThisRatecard = false
    }
  }, [id])

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Network Tariffs', href: '/transport-price-master', current: false },
    { name: 'Edit Network Rate Card', href: '#', current: true },
  ]

  if (isLoading) {
    return (
      <div className='flex flex-col h-[calc(100vh-64px)]'>
        <PageHeader header='Edit Network Rate Card' pages={pages} />
        <div className='flex items-center justify-center flex-1'>
          <FloxSpinner size='md' />
        </div>
      </div>
    )
  }

  if (loadError || !loadedData) {
    return (
      <div className='flex flex-col h-[calc(100vh-64px)]'>
        <PageHeader header='Edit Network Rate Card' pages={pages} />
        <div className='flex items-center justify-center flex-1'>
          <p className='text-red-500'>{loadError || 'Failed to load'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-[calc(100vh-64px)]'>
      <PageHeader header='Edit Network Rate Card' pages={pages} />
      <NetworkRatecardEditor
        mode='edit'
        ratecardId={id}
        initialNetworkName={loadedData.networkName}
        initialCountryCode={loadedData.countryCode}
        initialCurrency={loadedData.currency}
        initialEffectiveFrom={loadedData.effectiveFrom}
        initialEffectiveTo={loadedData.effectiveTo}
        initialNotes={loadedData.notes}
        initialIsActive={loadedData.isActive}
        initialIsLocalRatecard={loadedData.isLocalRatecard}
        initialZones={loadedData.zones}
        initialSurcharges={loadedData.surcharges}
        saveEndpoint={NETWORK_RATECARD_UPDATE}
        saveButtonLabel='Save Changes'
        onSaveSuccess={() => navigate('/transport-price-master')}
        onCancel={() => navigate('/transport-price-master')}
      />
    </div>
  )
}
